import { useRef, useState, useLayoutEffect } from 'react';
import Icon from './Icon.jsx';

// THE page tab strip. One implementation so every page's header behaves the
// same — same hover, same gliding gradient underline, same icon treatment.
//
// Before this existed, AlertsPage and GoalsPage each had a private `TabBtn`
// (a flat 2px border-bottom, no icon, no motion) while ViewPage and
// AppAnalyticsPage used the shared `.subtabs` CSS with the sliding underline.
// Three strips, three behaviours, on pages a client moves between in one
// session. If you need a tab strip, use this — don't grow a fourth.
//
// tabs: [{ id, title, icon? }]  — `icon` is a name from components/icons.jsx
// (docs/ICON_SYSTEM.md). Tabs animate their glyph on hover exactly like the
// sidebar rows do; the rule lives in index.css next to the nav-row one.
export default function SubTabs({ tabs, activeId, onSelect, isMobile, style }) {
  const wrapRef = useRef(null);
  const [u, setU] = useState({ left: 0, width: 0, show: false });

  // Reposition the sliding underline whenever the active tab (or the tab set)
  // changes. Pure geometry read → state; it never scrolls anything.
  useLayoutEffect(() => {
    const el = wrapRef.current?.querySelector('[data-active="1"]');
    if (!el) { setU((s) => ({ ...s, show: false })); return; }
    setU({ left: el.offsetLeft + 10, width: Math.max(0, el.offsetWidth - 20), show: true });
  }, [activeId, tabs]);

  // Centre the active tab within the (horizontally scrollable) strip on a real
  // tab change — scrolling the strip itself, NOT any ancestor. An earlier
  // el.scrollIntoView() also drove the nearest *vertical* scroller, and because
  // `tabs` is often a fresh array on every render (polls, soft refreshes) it
  // kept yanking the page back to the top mid-scroll. Depending only on
  // activeId means it fires on genuine switches.
  useLayoutEffect(() => {
    const strip = wrapRef.current;
    const el = strip?.querySelector('[data-active="1"]');
    if (!strip || !el) return;
    const left = el.offsetLeft - (strip.clientWidth - el.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [activeId]);

  return (
    <div className="subtabs" ref={wrapRef} style={{ padding: isMobile ? '0 8px' : '0 14px', ...style }}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          data-active={t.id === activeId ? '1' : undefined}
          className={`subtab${t.id === activeId ? ' active' : ''}`}
          onClick={() => { if (t.id !== activeId) onSelect(t.id); }}
        >
          {t.icon ? <Icon name={t.icon} size={15} /> : null}
          {t.title}
        </button>
      ))}
      <span
        className="subtab-underline"
        style={{ transform: `translateX(${u.left}px)`, width: u.width, opacity: u.show ? 1 : 0 }}
      />
    </div>
  );
}
